import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Github, Twitter, Youtube, MessageCircle, Gamepad2 } from 'lucide-react';
import SupportButtons from '@/components/support/SupportButtons';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';
import AdDisplay from '@/components/ads/AdDisplay';

const Footer = () => {
  const { settings, isLoading } = useWebsiteSettings();
  
  // Don't render until settings are loaded to prevent flash
  if (isLoading) {
    return (
      <footer className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded mb-4"></div>
            <div className="h-4 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
          </div>
        </div>
      </footer>
    );
  }
  
  const siteName = settings.site_name || 'TerraGamers';
  const footerText = settings.footer_text || `© ${new Date().getFullYear()} ${siteName}. All rights reserved.`;
  const logoUrl = settings.logo_url;
  
  // Get logo sizing settings with defaults (same as header)
  const logoMobileHeight = settings.logo_mobile_height || 48;
  const logoDesktopHeight = settings.logo_desktop_height || 64;
  const logoMaxWidth = settings.logo_max_width || 200;
  const logoMaxWidthDesktop = settings.logo_max_width_desktop || 280;

  // Create dynamic styles for footer logo
  const logoContainerStyle = {
    height: `${logoMobileHeight}px`,
    '--desktop-height': `${logoDesktopHeight}px`,
  } as React.CSSProperties & { '--desktop-height': string };

  const logoImageStyle = {
    maxWidth: `${logoMaxWidth}px`,
    '--desktop-max-width': `${logoMaxWidthDesktop}px`,
  } as React.CSSProperties & { '--desktop-max-width': string };
  
  // Get social media URLs with strict validation
  const socialUrls = {
    discord_url: settings.discord_url,
    twitter_url: settings.twitter_url,
    youtube_url: settings.youtube_url,
    github_url: settings.github_url,
  };
  
  const socialLinks = [
    { 
      icon: MessageCircle, 
      url: socialUrls.discord_url, 
      label: 'Discord',
      color: 'hover:text-indigo-400'
    },
    { 
      icon: Twitter, 
      url: socialUrls.twitter_url, 
      label: 'Twitter',
      color: 'hover:text-sky-400'
    },
    { 
      icon: Youtube, 
      url: socialUrls.youtube_url, 
      label: 'YouTube',
      color: 'hover:text-red-400'
    },
    { 
      icon: Github, 
      url: socialUrls.github_url, 
      label: 'GitHub',
      color: 'hover:text-gray-300'
    },
  ].filter(link => {
    const hasValidUrl = Boolean(
      link.url && 
      link.url.trim() !== '' && 
      link.url !== 'undefined' && 
      link.url !== 'null' &&
      link.url.startsWith('http')
    );
    return hasValidUrl;
  });

  // Popular categories - Fixed URLs to use /category/ instead of /categories/
  const popularCategories = [
    { name: 'Action Games', url: '/category/action' },
    { name: 'Racing Games', url: '/category/racing' },
    { name: 'Puzzle Games', url: '/category/puzzle' },
    { name: 'Adventure Games', url: '/category/adventure' },
    { name: 'Sports Games', url: '/category/sports' },
    { name: 'Strategy Games', url: '/category/strategy' },
  ];

  // Check if we should show custom logo - same validation as header
  const showCustomLogo = Boolean(
    logoUrl && 
    logoUrl.trim() !== '' && 
    logoUrl !== 'undefined' && 
    logoUrl !== 'null' &&
    logoUrl.startsWith('http')
  );

  return (
    <>
      {/* Footer Banner Ad */}
      <div className="w-full bg-gray-50 border-t border-gray-200">
        <AdDisplay position="footer" />
      </div>

      <footer className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(120,119,198,0.3),transparent_50%),radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.15),transparent_50%),radial-gradient(circle_at_40%_40%,rgba(120,200,255,0.2),transparent_50%)]"></div>
        
        {/* Main Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* About Section */}
            <div className="space-y-6 lg:col-span-1">
              <div className="flex items-center space-x-3">
                {showCustomLogo ? (
                  <div 
                    className="flex-shrink-0 sm:h-[var(--desktop-height)]"
                    style={logoContainerStyle}
                  >
                    <img 
                      src={logoUrl} 
                      alt={siteName}
                      className="h-full w-auto object-contain sm:max-w-[var(--desktop-max-width)] drop-shadow-lg"
                      style={logoImageStyle}
                      loading="eager"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Gamepad2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                        {siteName}
                      </span>
                      <p className="text-gray-300 text-sm">
                        Play. Discover. Enjoy.
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              <p className="text-gray-300 leading-relaxed text-sm">
                {settings.site_description || 'Discover thousands of free online games. From action-packed adventures to mind-bending puzzles, we\'ve got the perfect game for every gamer.'}
              </p>
              
              {/* Support Section */}
              <div className="space-y-4">
                <p className="text-gray-300 font-medium text-sm">Support our work:</p>
                <SupportButtons />
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-6">
              <h3 className="font-semibold text-white text-lg bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Quick Links
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link 
                    to="/about" 
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact" 
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/privacy" 
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/terms" 
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Popular Categories */}
            <div className="space-y-6">
              <h3 className="font-semibold text-white text-lg bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Popular Categories
              </h3>
              <ul className="space-y-3">
                {popularCategories.map((category) => (
                  <li key={category.name}>
                    <Link 
                      to={category.url} 
                      className="text-gray-300 hover:text-white transition-colors duration-200 text-sm hover:translate-x-1 inline-block"
                    >
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Stay Connected section with social links */}
            <div className="space-y-6">
              <h3 className="font-semibold text-white text-lg bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent">
                Stay Connected
              </h3>
              <p className="text-gray-300 text-sm">
                Join our community and never miss the latest games and updates.
              </p>
              
              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="flex space-x-4">
                  {socialLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2 text-gray-400 transition-all duration-300 hover:scale-110 ${link.color} rounded-lg hover:bg-white/10`}
                      aria-label={link.label}
                    >
                      <link.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              )}
              
              <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-white/10">
                <p className="text-gray-300 text-xs">
                  Follow us on social media for daily game recommendations and community updates.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700/50 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-gray-400 text-sm text-center sm:text-left">
              {footerText}
            </p>
            <p className="text-gray-400 text-sm flex items-center">
              Made with <Heart className="w-4 h-4 text-red-500 mx-1 animate-pulse" /> for gamers worldwide
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
